// src/components/cards/ClientPetsCard.tsx - Complete original version with FAB
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Pets as PetsIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Pet, Client } from '../../services/api';
import { StyledIconButton } from '../common/Buttons';
import { ClientPetsList, ExtendedPet } from '../lists/BeingsList';
import { FloatingAccessButton } from '../common/FloatingAccessButton';

interface ClientPetsCardProps {
  pets: Pet[];
  client?: Client;
  onPetClick?: (petId: number) => void;
  title?: string;
  // Additional action handlers to support BeingsList functionality
  onEdit?: (id: number) => void;
  onView?: (id: number) => void;
  onDelete?: (id: number) => void;
  onNewBooking?: (id: number) => void;
  onViewBookings?: (id: number) => void;
  onViewAdmin?: (id: number) => void;
  onViewMedical?: (id: number) => void;
  onOwnerClick?: (clientId: number) => void;
  onNewPet?: () => void;
}

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '2rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  color: theme.palette.text.secondary,
  gap: '1.5rem',
}));

const BeingsListContainer = styled(Box)({
  marginTop: '8px',
  // Remove the card styling since BeingsList handles its own layout
  '& .MuiCard-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
});

const ClientPetsCard: React.FC<ClientPetsCardProps> = ({
  pets,
  client,
  onPetClick,
  title = 'Pets',
  onEdit,
  onView,
  onDelete,
  onNewBooking,
  onViewBookings,
  onViewAdmin,
  onViewMedical,
  onOwnerClick,
  onNewPet,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Filter out null/undefined pets before processing
  const validPets = pets
    ? pets.filter((pet) => pet !== null && pet !== undefined)
    : [];

  // Handle FAB click with ripple effect
  const handleFabClick = () => {
    if (onNewPet) {
      onNewPet();
    } else if (client?.id) {
      // Fallback navigation if onNewPet not provided
      navigate(`/clients/${client.id}/newpet`);
    } else {
      console.error('Unable to determine client ID for new pet navigation');
    }
  };

  const handleNewBooking = () => {
    navigate(`/clients/${client?.id}/newpet`);
  };

  return (
    <StyledCard>
      {validPets.length === 0 ? (
        <EmptyState>
          <Typography
            variant="h4"
            sx={{ fontSize: '2rem', color: `${theme.palette.primary.main}` }}
          >
            0 Pets found
          </Typography>
          <Typography variant="h4" sx={{ fontSize: '2rem' }} gutterBottom>
            Add a pet for {client?.firstName}
          </Typography>
          <StyledIconButton
            onClick={handleNewBooking}
            variant="delete"
            sx={{
              width: '3.5rem',
              height: '3.5rem',
              backgroundColor: theme.palette.secondary.main,
              '& svg': {
                color: '#fff',
                fontSize: '3rem',
              },
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
              },
            }}
            aria-label="Add new booking"
          >
            <AddIcon />
          </StyledIconButton>
        </EmptyState>
      ) : (
        <BeingsListContainer>
          {/* Use ClientPetsList specifically for client's pets context */}
          <ClientPetsList
            items={validPets as ExtendedPet[]}
            type="pets"
            client={client}
            onEdit={onEdit}
            onView={onView}
            onDelete={onDelete}
            onNewBooking={onNewBooking}
            onViewBookings={onViewBookings}
            onViewAdmin={onViewAdmin}
            onViewMedical={onViewMedical}
            onOwnerClick={onOwnerClick}
            onPetClick={onPetClick}
          />
        </BeingsListContainer>
      )}

      {/* Fixed position FAB - only show when pets exist */}
      {validPets.length > 0 && (
        <FloatingAccessButton
          icon={<AddIcon />}
          hoverIcon={<PetsIcon />}
          onClick={handleFabClick}
          aria-label="Add new pet"
          iconScale={1.5}
          backgroundColor={theme.palette.secondary.main}
          hoverBackgroundColor={theme.palette.secondary.dark}
          bottom="2rem"
          right="10%"
          sx={{
            '@media (max-width: 600px)': {
              bottom: '1rem',
              right: '1rem',
            },
            '@media (min-width: 601px)': {
              width: '75px',
              height: '75px',
              minWidth: '75px',
              minHeight: '75px',
            },
          }}
        />
      )}
    </StyledCard>
  );
};

export default ClientPetsCard;
